import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { MessageService } from 'primeng/api';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { ReminderCadenceService, StatusReminderConfig } from './reminder-cadence.service';

/** One status row plus a per-row saving flag. */
interface ReminderRow extends StatusReminderConfig {
    saving?: boolean;
}

/**
 * Pro "Follow-up Reminders" settings tab. Lists every status; for each, the
 * admin turns reminders on and sets how often to be nudged + when to stop.
 * Wires GET/PUT /api/reminders/config.
 */
@Component({
    selector: 'app-reminder-cadence',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ButtonModule,
        InputNumberModule,
        ProgressSpinnerModule,
        ToastModule,
        ToggleSwitchModule,
        TooltipModule,
    ],
    providers: [MessageService],
    template: `
    <p-toast></p-toast>

    <div class="rem-wrap">
      <div class="rem-head">
        <h2>Follow-up reminders</h2>
        <p>
          Get nudged to follow up with a lead while its case sits on a status.
          Turn it on for a status, choose how often you want reminding, and when
          the reminders should stop.
        </p>
      </div>

      <div *ngIf="loading" class="rem-loading">
        <p-progressSpinner styleClass="rem-spin" strokeWidth="4"></p-progressSpinner>
      </div>

      <div *ngIf="!loading">
        <div *ngIf="!rows.length" class="rem-empty">
          No statuses yet. Add statuses under <b>Settings &rsaquo; Statuses</b> first,
          then set reminders for them here.
        </div>

        <div class="rem-list" *ngIf="rows.length">
          <div class="rem-item" *ngFor="let r of rows" [class.on]="r.enabled">
            <div class="rem-item__main">
              <span class="rem-status">
                <i class="dot" [style.background]="r.color || '#94a3b8'"></i>
                {{ r.statusName }}
              </span>

              <div class="rem-controls">
                <label class="rem-toggle">
                  <p-toggleswitch [(ngModel)]="r.enabled"></p-toggleswitch>
                  <span>{{ r.enabled ? 'On' : 'Off' }}</span>
                </label>

                <div class="rem-field" *ngIf="r.enabled">
                  <span>Every</span>
                  <p-inputnumber [(ngModel)]="r.frequencyDays" [min]="1" [max]="365"
                                 [showButtons]="true" buttonLayout="horizontal"
                                 decrementButtonClass="p-button-text" incrementButtonClass="p-button-text"
                                 inputStyleClass="rem-num-in"></p-inputnumber>
                  <span>day(s)</span>
                </div>

                <div class="rem-field" *ngIf="r.enabled">
                  <span>Stop after</span>
                  <p-inputnumber [(ngModel)]="r.maxDays" [min]="1" [max]="365"
                                 [showButtons]="true" buttonLayout="horizontal"
                                 decrementButtonClass="p-button-text" incrementButtonClass="p-button-text"
                                 inputStyleClass="rem-num-in"></p-inputnumber>
                  <span>day(s)</span>
                </div>

                <button pButton type="button" label="Save" class="p-button-sm rem-save"
                        [loading]="!!r.saving" (click)="save(r)"></button>
              </div>
            </div>

            <div class="rem-item__note" *ngIf="r.enabled">
              {{ summary(r) }}
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
    styles: [`
    .rem-wrap { display:flex; flex-direction:column; gap:1rem; }
    .rem-head h2 { margin:0 0 .25rem; font-size:var(--gh-fs-19); font-weight:700; color:var(--text-color); }
    .rem-head p { margin:0; max-width:52rem; color:var(--text-color-secondary); font-size:var(--gh-fs-14); line-height:1.5; }

    .rem-loading { display:flex; justify-content:center; padding:2.5rem 0; }
    :host ::ng-deep .rem-spin { width:2.5rem; height:2.5rem; }

    .rem-empty { padding:1.25rem; border:1px dashed var(--surface-border); border-radius:var(--gh-r-lg);
                 color:var(--text-color-secondary); font-size:var(--gh-fs-14); }

    .rem-list { display:flex; flex-direction:column; gap:.6rem; }

    .rem-item { border:1px solid var(--surface-border); border-radius:var(--gh-r-lg); padding:.85rem 1rem;
                background:transparent; transition:border-color .15s ease; }
    .rem-item.on { border-color:color-mix(in srgb, var(--primary-color) 45%, var(--surface-border)); }

    .rem-item__main { display:flex; align-items:center; gap:1rem; flex-wrap:wrap; }
    .rem-status { display:inline-flex; align-items:center; gap:.5rem; font-weight:600; color:var(--text-color);
                  min-width:12rem; flex:0 0 auto; }
    .rem-status .dot { width:.7rem; height:.7rem; border-radius:50%; display:inline-block; }

    .rem-controls { display:flex; align-items:center; gap:1rem; flex-wrap:wrap; margin-left:auto; }
    .rem-toggle { display:inline-flex; align-items:center; gap:.5rem; color:var(--text-color-secondary); font-size:var(--gh-fs-14); }
    .rem-field { display:inline-flex; align-items:center; gap:.5rem; color:var(--text-color-secondary); font-size:var(--gh-fs-14); }
    :host ::ng-deep .rem-num-in { width:3.5rem; text-align:center; }
    .rem-save { min-width:5rem; }

    .rem-item__note { margin-top:.6rem; padding-top:.55rem; border-top:1px dashed var(--surface-border);
                      color:var(--text-color-secondary); font-size:var(--gh-fs-13); }
  `]
})
export class ReminderCadenceComponent implements OnInit {

    private service = inject(ReminderCadenceService);
    private toast = inject(MessageService);

    rows: ReminderRow[] = [];
    loading = false;

    ngOnInit(): void {
        this.load();
    }

    load(): void {
        this.loading = true;
        this.service.listConfig().subscribe({
            next: rows => {
                this.rows = (rows || []).map(r => ({
                    ...r,
                    frequencyDays: r.frequencyDays ?? 2,
                    maxDays: r.maxDays ?? 14,
                }));
                this.loading = false;
            },
            error: () => {
                this.rows = [];
                this.loading = false;
                this.toast.add({ severity: 'error', summary: 'Load failed', detail: 'Could not load reminder settings.' });
            }
        });
    }

    /** Plain-English recap of the row's cadence. */
    summary(r: ReminderRow): string {
        const every = r.frequencyDays === 1 ? 'every day' : `every ${r.frequencyDays} days`;
        return `Reminds you ${every} to contact the lead, stopping after ${r.maxDays} day(s) on this status.`;
    }

    save(r: ReminderRow): void {
        if (!r.frequencyDays || r.frequencyDays < 1) r.frequencyDays = 1;
        if (!r.maxDays || r.maxDays < 1) r.maxDays = 1;
        if (r.enabled && r.maxDays < r.frequencyDays) {
            this.toast.add({
                severity: 'warn', summary: 'Check the days',
                detail: '"Stop after" should be at least the reminder frequency, or no reminder can fire.'
            });
            return;
        }

        r.saving = true;
        this.service.saveConfig(r.statusId, {
            enabled: r.enabled,
            frequencyDays: r.frequencyDays,
            maxDays: r.maxDays,
        }).subscribe({
            next: () => {
                r.saving = false;
                this.toast.add({
                    severity: 'success', summary: 'Saved',
                    detail: r.enabled
                        ? `Reminders are on for "${r.statusName}".`
                        : `Reminders are off for "${r.statusName}".`
                });
            },
            error: () => {
                r.saving = false;
                this.toast.add({ severity: 'error', summary: 'Save failed', detail: `Could not save "${r.statusName}".` });
            }
        });
    }
}
